import React, { useState, useEffect, useCallback, useRef } from 'react';
import { accentUtils, showDialog } from '../services/HelperService';
import {
    Button,
    Form,
    FormGroup,
    Label,
    Input,
    Spinner,
    Alert,
    Row,
    Col,
    ModalBody,
    FormFeedback,
    InputGroup,
    InputGroupText
} from 'reactstrap';
import { AccentDialog } from '../dialogs/AccentDialog';
import { queryNoTracking } from '../services/DataService';
import { usePosition } from '../controls/AccentDisplay';
import { AccentNumberUnbound } from '../controls/unbound/AccentNumberUnbound';

const query_GetAuditLog = queryNoTracking("GetAuditLog");
const query_GetAuditLogEntities = queryNoTracking("GetAuditLogEntities");

export const showAuditLog = ({ entityType, id, isParentID } = {}) => {
    const safeId = id ?? '';
    showDialog(<AuditLogDlg
        entityNameFilter={entityType}
        entityIDFilter={isParentID ? undefined : `${safeId}`}
        parentEntityIDFilter={isParentID ? `${safeId}` : undefined}
    />);
};

const AuditLogDlg = ({ onClose, entityNameFilter, entityIDFilter, parentEntityIDFilter }) => {
    const isPreFiltered = !!(entityNameFilter && (entityIDFilter || parentEntityIDFilter));
    const preFilteredIdType = parentEntityIDFilter ? 'parent' : 'entity';

    // --- State Variables ---
    const [searchMode, setSearchMode] = useState(isPreFiltered ? 'entity' : 'transaction');
    const [transactionId, setTransactionId] = useState('');
    const [entityType, setEntityType] = useState(entityNameFilter || '');
    const [entityId, setEntityId] = useState(entityIDFilter || '');
    const [parentEntityId, setParentEntityId] = useState(parentEntityIDFilter || '');
    const [entityIdType, setEntityIdType] = useState(isPreFiltered ? preFilteredIdType : 'entity');

    // --- State for Entity Types Dropdown ---
    const [entityTypesList, setEntityTypesList] = useState([]);
    const [isFetchingEntities, setIsFetchingEntities] = useState(false);
    const [entityFetchError, setEntityFetchError] = useState(null);

    // --- Standard State ---
    const [auditLogs, setAuditLogs] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // --- New State for iFrame Search and Controls ---
    const [iframeSearchTerm, setIframeSearchTerm] = useState('');
    const [matchCount, setMatchCount] = useState(0);
    const [isIframeLoaded, setIsIframeLoaded] = useState(false);
    const iframeRef = useRef(null);

    const pos = usePosition("auditReportID");

    // --- Helper Functions for iFrame Manipulation ---
    const expandAll = useCallback(() => {
        if (!iframeRef.current) return;

        const iframeDoc = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;
        const collapseElements = iframeDoc.querySelectorAll('.collapse');
        const toggleButtons = iframeDoc.querySelectorAll('.toggle-details-btn');

        collapseElements.forEach(element => {
            element.classList.add('show');
        });

        toggleButtons.forEach(button => {
            button.setAttribute('aria-expanded', 'true');
        });
    }, []);

    const collapseAll = useCallback(() => {
        if (!iframeRef.current) return;

        const iframeDoc = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;
        const collapseElements = iframeDoc.querySelectorAll('.collapse');
        const toggleButtons = iframeDoc.querySelectorAll('.toggle-details-btn');

        collapseElements.forEach(element => {
            element.classList.remove('show');
        });

        toggleButtons.forEach(button => {
            button.setAttribute('aria-expanded', 'false');
        });
    }, []);

    const clearIframeSearch = useCallback(() => {
        setIframeSearchTerm('');
        if (!iframeRef.current) return;

        const iframeDoc = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;

        // Remove all highlights - more thorough approach
        const highlightedElements = iframeDoc.querySelectorAll('.search-highlight');
        highlightedElements.forEach(element => {
            const parent = element.parentNode;
            const textNode = iframeDoc.createTextNode(element.textContent);
            parent.replaceChild(textNode, element);
            parent.normalize();
        });

        // Show all body rows (but not header rows)
        const allRows = iframeDoc.querySelectorAll('tbody tr');
        allRows.forEach(row => {
            row.classList.remove('row-hidden');
            row.style.display = ''; // Also clear any inline display styles
        });

        // Ensure header rows are always visible
        const headerRows = iframeDoc.querySelectorAll('thead tr');
        headerRows.forEach(row => {
            row.classList.remove('row-hidden');
            row.style.display = '';
        });

        // Hide no results message if it exists
        const noResultsDiv = iframeDoc.getElementById('no-results');
        if (noResultsDiv) {
            noResultsDiv.style.display = 'none';
        }

        setMatchCount(0);
    }, []);

    // utility to escape regex metacharacters in the search term
    const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    const highlightText = (element, searchTerm, doc) => {
        if (!searchTerm) return;

        const escaped = escapeRegExp(searchTerm);
        const regex = new RegExp(`(${escaped})`, 'gi');

        // Walk text nodes under the element
        const walker = doc.createTreeWalker(element, NodeFilter.SHOW_TEXT, null, false);
        const textNodes = [];
        let node;
        while ((node = walker.nextNode())) {
            // skip nodes already wrapped (avoid nesting)
            if (!node.parentElement || !node.parentElement.classList.contains('search-highlight')) {
                textNodes.push(node);
            }
        }

        textNodes.forEach((textNode) => {
            const text = textNode.nodeValue || '';
            if (!text) return;

            if (regex.test(text)) {
                const span = doc.createElement('span');
                // wrap matches with highlight spans
                span.innerHTML = text.replace(regex, '<span class="search-highlight">$1</span>');
                textNode.parentNode.replaceChild(span, textNode);
            }
        });
    };


const performIframeSearch = useCallback((term) => {
    if (!iframeRef.current || !term) {
        clearIframeSearch();
        return;
    }

    const iframeDoc = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;

    // Inject styles for search highlighting and hiding rows if not already present
    if (!iframeDoc.getElementById('search-styles')) {
        const style = iframeDoc.createElement('style');
        style.id = 'search-styles';
        style.textContent = '.search-highlight { background-color: #ffeb3b !important; padding: 2px 0; font-weight: bold; } ' +
            'tbody tr.row-hidden { display: none !important; } ' +
            'thead tr { display: table-row !important; } ' +
            '.no-results { text-align: center; padding: 2rem; color: #6c757d; font-style: italic; }';
        iframeDoc.head.appendChild(style);
    }

    // Clear previous highlights more thoroughly
    const previousHighlights = iframeDoc.querySelectorAll('.search-highlight');
    previousHighlights.forEach(element => {
        const parent = element.parentNode;
        const textNode = iframeDoc.createTextNode(element.textContent);
        parent.replaceChild(textNode, element);
        parent.normalize();
    });

    // Ensure all header rows remain visible
    const headerRows = iframeDoc.querySelectorAll('thead tr');
    headerRows.forEach(row => {
        row.classList.remove('row-hidden');
        row.style.display = '';
    });

    // Get only tbody rows (exclude thead rows from search/filtering)
    // More specific selectors for different table structures
    const mainRows = iframeDoc.querySelectorAll('tbody > tr.align-middle, tbody > tr:not(.change-details-row):not(.collapse):not([id^="details"])');

    let visibleRowCount = 0;
    let totalMatches = 0;

    // Process main rows and their detail rows
    mainRows.forEach((mainRow) => {
        // Find the corresponding detail row (usually the next sibling or by ID reference)
        let detailRow = null;

        // Try to find detail row by toggle button's href
        const toggleBtn = mainRow.querySelector('.toggle-details-btn');
        if (toggleBtn) {
            const targetId = toggleBtn.getAttribute('href');
            if (targetId && targetId.startsWith('#')) {
                detailRow = iframeDoc.querySelector(targetId);
            }
        }

        // If not found by ID, check next sibling
        if (!detailRow) {
            const nextRow = mainRow.nextElementSibling;
            if (nextRow && (nextRow.classList.contains('change-details-row') ||
                nextRow.classList.contains('collapse') ||
                nextRow.id?.startsWith('details'))) {
                detailRow = nextRow;
            }
        }

        let rowHasMatch = false;

        // Search in main row cells
        const cells = mainRow.querySelectorAll('td');
        cells.forEach(cell => {
            if (cell.querySelector('.toggle-details-btn')) return; // Skip toggle button cell

            const text = cell.textContent || '';
            if (text.toLowerCase().includes(term.toLowerCase())) {
                rowHasMatch = true;
                highlightText(cell, term, iframeDoc);
                totalMatches++;
            }
        });

        // Search in detail row
        if (detailRow) {
            const detailCells = detailRow.querySelectorAll('td');
            detailCells.forEach(cell => {
                const text = cell.textContent || '';
                if (text.toLowerCase().includes(term.toLowerCase())) {
                    rowHasMatch = true;
                    highlightText(cell, term, iframeDoc);
                    totalMatches++;
                }
            });
        }

        // Show/hide rows based on match
        if (rowHasMatch) {
            mainRow.classList.remove('row-hidden');
            mainRow.style.display = ''; // Clear any inline styles
            if (detailRow) {
                detailRow.classList.remove('row-hidden');
                detailRow.style.display = '';
            }
            visibleRowCount++;
        } else {
            mainRow.classList.add('row-hidden');
            if (detailRow) detailRow.classList.add('row-hidden');
        }
    });

    // Create or update no results message
    let noResultsDiv = iframeDoc.getElementById('no-results');
    if (!noResultsDiv && visibleRowCount === 0) {
        noResultsDiv = iframeDoc.createElement('div');
        noResultsDiv.id = 'no-results';
        noResultsDiv.className = 'no-results';
        noResultsDiv.textContent = 'No results found for your search.';
        const tableContainer = iframeDoc.querySelector('.container-fluid') || iframeDoc.body;
        tableContainer.insertBefore(noResultsDiv, tableContainer.firstChild);
    } else if (noResultsDiv) {
        noResultsDiv.style.display = visibleRowCount === 0 ? 'block' : 'none';
    }

    setMatchCount(totalMatches);
}, [clearIframeSearch]);

// --- Handle iFrame search with debounce ---
useEffect(() => {
    if (isIframeLoaded && iframeRef.current) {
        const debounceTimer = setTimeout(() => {
            performIframeSearch(iframeSearchTerm);
        }, 300);

        return () => clearTimeout(debounceTimer);
    }
}, [iframeSearchTerm, isIframeLoaded, performIframeSearch]);

// --- Handle iFrame load ---
const handleIframeLoad = useCallback(() => {
    setIsIframeLoaded(true);
    // Clear any previous search state
    setIframeSearchTerm('');
    setMatchCount(0);

    // Ensure headers are visible on initial load
    if (iframeRef.current) {
        const iframeDoc = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;
        const headerRows = iframeDoc.querySelectorAll('thead tr');
        headerRows.forEach(row => {
            row.classList.remove('row-hidden');
            row.style.display = '';
        });
    }
}, []);

// --- Reset iframe state when new logs are loaded ---
useEffect(() => {
    if (auditLogs) {
        setIsIframeLoaded(false);
        setIframeSearchTerm('');
        setMatchCount(0);
    }
}, [auditLogs]);

// --- Fetch Entity Types on Mount ---
useEffect(() => {
    const fetchEntities = async () => {
        if (isPreFiltered) return;

        setIsFetchingEntities(true);
        setEntityFetchError(null);
        try {
            const entities = await query_GetAuditLogEntities.getAll();
            setEntityTypesList(entities || []);
            if (entityNameFilter && !entityIDFilter && !parentEntityIDFilter) {
                setEntityType(entityNameFilter);
            } else if (!entityNameFilter) {
                setEntityType('');
            }
        } catch (err) {
            console.error("Error fetching audit log entities:", err);
            setEntityFetchError("Failed to load entity types.");
            setEntityTypesList([]);
        } finally {
            setIsFetchingEntities(false);
        }
    };

    fetchEntities();
}, [isPreFiltered]);

// --- Fetch Audit Logs Logic ---
const fetchAuditLogs = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setAuditLogs("");

    const guidRegex = /^[\{]?[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}[\}]?$/;

    let searchParams = {};
    let isValid = false;
    let validationError = null;

    if (searchMode === 'transaction') {
        const trimmedTransactionId = transactionId.trim();
        if (trimmedTransactionId) {
            if (guidRegex.test(trimmedTransactionId)) {
                searchParams = { transactionId: trimmedTransactionId };
                isValid = true;
            } else {
                validationError = "Please enter a valid Transaction GUID.";
            }
        } else if (!isPreFiltered) {
            validationError = "Please enter a Transaction ID.";
        }
    } else if (searchMode === 'entity') {
        const currentEntityType = entityType;
        const currentEntityId = entityId;
        const currentParentEntityId = parentEntityId;

        if (!currentEntityType && !isPreFiltered) {
            validationError = "Please select an Entity Type.";
        } else if (entityIdType === 'entity' && (currentEntityId === null || currentEntityId === undefined || currentEntityId === '') && !isPreFiltered) {
            validationError = "Please enter an Entity ID.";
        } else if (entityIdType === 'parent' && (currentParentEntityId === null || currentParentEntityId === undefined || currentParentEntityId === '') && !isPreFiltered) {
            validationError = "Please enter a Parent Entity ID.";
        } else {
            const effectiveEntityType = isPreFiltered ? (entityNameFilter || '') : currentEntityType;

            if (effectiveEntityType) {
                if (entityIdType === 'entity' && (currentEntityId !== null && currentEntityId !== undefined && currentEntityId !== '' || entityIDFilter)) {
                    searchParams = { entityType: effectiveEntityType, entityId: isPreFiltered ? entityIDFilter : currentEntityId };
                    isValid = true;
                } else if (entityIdType === 'parent' && (currentParentEntityId !== null && currentParentEntityId !== undefined && currentParentEntityId !== '' || parentEntityIDFilter)) {
                    searchParams = { entityType: effectiveEntityType, parentEntityId: isPreFiltered ? parentEntityIDFilter : currentParentEntityId };
                    isValid = true;
                }
            }
        }
    }

    if (!isPreFiltered && !isValid && validationError) {
        setError(validationError);
        setIsLoading(false);
        return;
    }

    if (!isValid && isPreFiltered) {
        const preFilterEntityType = entityNameFilter || '';
        if (preFilterEntityType) {
            if (entityIdType === 'entity' && entityIDFilter) {
                searchParams = { entityType: preFilterEntityType, entityId: entityIDFilter };
                isValid = true;
            } else if (entityIdType === 'parent' && parentEntityIDFilter) {
                searchParams = { entityType: preFilterEntityType, parentEntityId: parentEntityIDFilter };
                isValid = true;
            }
        }
        if (!isValid) {
            setError("Invalid pre-filter criteria provided.");
            setIsLoading(false);
            return;
        }
    }

    if (isValid) {
        try {
            console.log("Fetching with params:", searchParams);
            const results = await query_GetAuditLog.getAll(searchParams);
            setAuditLogs(results || "");
            if (!results || results.length === 0) {
                setError("No audit logs found for the specified criteria.");
            }
        } catch (err) {
            console.error("Error fetching audit logs:", err);
            setError(err.message || "Failed to fetch audit logs. Please try again.");
        } finally {
            setIsLoading(false);
        }
    } else {
        if (!error && !isPreFiltered) setError("Please provide valid search criteria.");
        setIsLoading(false);
    }
}, [
    searchMode, transactionId, entityType, entityId, parentEntityId, entityIdType,
    isPreFiltered, entityNameFilter, entityIDFilter, parentEntityIDFilter
]);

// --- Trigger fetch automatically if pre-filtered ---
useEffect(() => {
    if (isPreFiltered) {
        if (entityNameFilter) {
            setEntityType(entityNameFilter);
        }
        fetchAuditLogs();
    }
}, [isPreFiltered, entityNameFilter, entityIDFilter, parentEntityIDFilter]);

const handleSearchClick = (e) => {
    e.preventDefault();
    if (!isPreFiltered) {
        fetchAuditLogs();
    }
};

const handleEntityIdTypeChange = (newType) => {
    setEntityIdType(newType);
    if (newType === 'entity') setParentEntityId('');
    else setEntityId('');
    setError(null);
};

const reportHeight = pos.toScreenHeight - 85; // Adjusted for control bar

// --- Component Rendering ---
return (
    <AccentDialog
        headerContent="View Audit History"
        onClose={onClose}
        relativeWidth={d => d.width - 80}
        relativeHeight={d => d.height - 80}
        actionsContent={
            !isPreFiltered ? [
                <Button key="search" color="primary" onClick={handleSearchClick} disabled={isLoading || isFetchingEntities}>
                    {isLoading ? <Spinner size="sm" /> : 'Search'}
                </Button>
            ] : []
        }
    >
        <ModalBody>
            <Form onSubmit={handleSearchClick}>
                <Row className="g-3 mb-3">
                    {/* Column 1: Search Method Radios */}
                    <Col md="auto">
                        <FormGroup tag="fieldset" disabled={isPreFiltered || isLoading}>
                            <Label className="fw-bold">Method</Label>
                            <FormGroup check>
                                <Input
                                    name="searchMode" type="radio" value="transaction" id="radioTransaction"
                                    checked={searchMode === 'transaction'}
                                    onChange={() => { setSearchMode('transaction'); setError(null); }}
                                    disabled={isPreFiltered || isLoading}
                                />{' '}<Label check for="radioTransaction">Transaction</Label>
                            </FormGroup>
                            <FormGroup check>
                                <Input
                                    name="searchMode" type="radio" value="entity" id="radioEntity"
                                    checked={searchMode === 'entity'}
                                    onChange={() => { setSearchMode('entity'); setError(null); }}
                                    disabled={isPreFiltered || isLoading}
                                />{' '}<Label check for="radioEntity">Entity</Label>
                            </FormGroup>
                        </FormGroup>
                    </Col>

                    {/* Column 2: Transaction ID (Conditional) */}
                    {searchMode === 'transaction' && (
                        <Col md={6} lg={5}>
                            <FormGroup>
                                <Label for="transactionIdInput">Transaction ID</Label>
                                <Input
                                    bsSize="sm"
                                    type="text" id="transactionIdInput" value={transactionId}
                                    onChange={(e) => setTransactionId(e.target.value)}
                                    placeholder="Enter Transaction GUID"
                                    disabled={isPreFiltered || isLoading}
                                    invalid={!!error && (error.includes("Transaction ID") || error.includes("GUID"))}
                                />
                                <FormFeedback>{error && (error.includes("Transaction ID") || error.includes("GUID")) ? error : ''}</FormFeedback>
                            </FormGroup>
                        </Col>
                    )}

                    {/* Columns for Entity Search (Conditional) */}
                    {searchMode === 'entity' && (
                        <>
                            {/* Column 3: Entity Type (Dropdown) */}
                            <Col md={4} lg={3}>
                                <FormGroup>
                                    <Label for="entityTypeSelect">Entity Type</Label>
                                    <Input
                                        bsSize="sm"
                                        type="select"
                                        id="entityTypeSelect"
                                        value={entityType}
                                        onChange={(e) => setEntityType(e.target.value)}
                                        disabled={isPreFiltered || isLoading || isFetchingEntities}
                                        invalid={!!error && error.includes("Entity Type")}
                                    >
                                        <option value="">
                                            {isFetchingEntities ? 'Loading types...' : (entityFetchError ? 'Error loading' : '-- Select Type --')}
                                        </option>
                                        {!isFetchingEntities && !entityFetchError && entityTypesList.map((name) => (
                                            <option key={name} value={name}>
                                                {name}
                                            </option>
                                        ))}
                                    </Input>
                                    {entityFetchError && !isFetchingEntities && (
                                        <div className="text-danger small mt-1">{entityFetchError}</div>
                                    )}
                                    <FormFeedback>{error && error.includes("Entity Type") ? error : ''}</FormFeedback>
                                </FormGroup>
                            </Col>

                            {/* Column 4: ID Type Radios */}
                            <Col md="auto">
                                <FormGroup tag="fieldset" disabled={isPreFiltered || isLoading}>
                                    <Label className="fw-bold">ID Type</Label>
                                    <FormGroup check>
                                        <Input
                                            name="entityIdType" type="radio" value="entity" id="radioIdEntity"
                                            checked={entityIdType === 'entity'}
                                            onChange={() => handleEntityIdTypeChange('entity')}
                                            disabled={isPreFiltered || isLoading}
                                        />{' '}<Label check for="radioIdEntity">Entity</Label>
                                    </FormGroup>
                                    <FormGroup check>
                                        <Input
                                            name="entityIdType" type="radio" value="parent" id="radioIdParent"
                                            checked={entityIdType === 'parent'}
                                            onChange={() => handleEntityIdTypeChange('parent')}
                                            disabled={isPreFiltered || isLoading}
                                        />{' '}<Label check for="radioIdParent">Parent</Label>
                                    </FormGroup>
                                </FormGroup>
                            </Col>

                            {/* Column 5: Entity ID / Parent ID Input */}
                            <Col md={4} lg={4}>
                                {entityIdType === 'entity' && (
                                    <FormGroup>
                                        <Label for="entityIdInput">Entity ID</Label>
                                        <AccentNumberUnbound
                                            dataTagName="entityIdInput"
                                            defaultValue={entityId}
                                            onChange={(newValue) => setEntityId(newValue ?? '')}
                                            placeHolder="Enter Entity ID"
                                            disabled={isPreFiltered || isLoading}
                                            invalid={!!error && error.includes("Entity ID")}
                                            isInt={true}
                                        />
                                        {!!error && error.includes("Entity ID") && <FormFeedback style={{ display: 'block' }}>{error}</FormFeedback>}
                                    </FormGroup>
                                )}
                                {entityIdType === 'parent' && (
                                    <FormGroup>
                                        <Label for="parentEntityIdInput">Parent Entity ID</Label>
                                        <AccentNumberUnbound
                                            dataTagName="parentEntityIdInput"
                                            defaultValue={parentEntityId}
                                            onChange={(newValue) => setParentEntityId(newValue ?? '')}
                                            placeHolder="Enter Parent ID"
                                            disabled={isPreFiltered || isLoading}
                                            invalid={!!error && error.includes("Parent Entity ID")}
                                            isInt={true}
                                        />
                                        {!!error && error.includes("Parent Entity ID") && <FormFeedback style={{ display: 'block' }}>{error}</FormFeedback>}
                                    </FormGroup>
                                )}
                            </Col>
                        </>
                    )}
                </Row>

                {/* Display general validation/fetch errors */}
                {error && !isLoading && !(error.includes("Entity Type") || error.includes("Entity ID") || error.includes("Parent Entity ID") || error.includes("Transaction ID") || error.includes("GUID") || error === "No audit logs found for the specified criteria.") && (
                    <Alert color="danger" className="mt-2">
                        {error}
                    </Alert>
                )}
                {error && !isLoading && error === "No audit logs found for the specified criteria." && accentUtils.isEmpty(auditLogs) && (
                    <Alert color="info" className="mt-2">
                        {error}
                    </Alert>
                )}
            </Form>

            <hr />

            {/* --- Results Section --- */}
            <div>
                {isLoading && (
                    <div className="text-center p-3">
                        <Spinner color="primary" />
                        <p>Loading history...</p>
                    </div>
                )}

                {/* Initial state prompts */}
                {!isLoading && !error && accentUtils.isEmpty(auditLogs) && !isPreFiltered && searchMode === 'transaction' && !transactionId && (
                    <Alert color="info">Please enter a Transaction ID and click Search.</Alert>
                )}
                {!isLoading && !error && accentUtils.isEmpty(auditLogs) && !isPreFiltered && searchMode === 'entity' && !entityType && (!entityId && !parentEntityId) && (
                    <Alert color="info">Please select Entity criteria and click Search.</Alert>
                )}

                {/* New Controls Bar and iFrame with results */}
                {!isLoading && !accentUtils.isEmpty(auditLogs) && (
                    <>
                        {/* Controls Bar */}
                        <Row className="mb-2 align-items-center">
                            <Col md={6}>
                                <InputGroup size="sm">
                                    <InputGroupText>
                                        <i className="bi bi-search"></i>
                                    </InputGroupText>
                                    <Input
                                        type="text"
                                        placeholder="Search within results..."
                                        value={iframeSearchTerm}
                                        onChange={(e) => setIframeSearchTerm(e.target.value)}
                                    />
                                    {iframeSearchTerm && (
                                        <Button
                                            color="secondary"
                                            outline
                                            size="sm"
                                            onClick={clearIframeSearch}
                                        >
                                            <i className="bi bi-x-circle"></i> Clear
                                        </Button>
                                    )}
                                </InputGroup>
                                {matchCount > 0 && iframeSearchTerm && (
                                    <small className="text-muted ms-2">
                                        Found {matchCount} match{matchCount !== 1 ? 'es' : ''}
                                    </small>
                                )}
                            </Col>
                            <Col md={6} className="text-end">
                                <Button
                                    color="info"
                                    size="sm"
                                    onClick={expandAll}
                                    disabled={!isIframeLoaded}
                                    className="me-2"
                                >
                                    <i className="bi bi-arrows-expand"></i> Expand All
                                </Button>
                                <Button
                                    color="secondary"
                                    size="sm"
                                    onClick={collapseAll}
                                    disabled={!isIframeLoaded}
                                >
                                    <i className="bi bi-arrows-collapse"></i> Collapse All
                                </Button>
                            </Col>
                        </Row>

                        {/* iFrame */}
                        <div
                            id="auditReportID"
                            style={{
                                width: '100%',
                                height: `${reportHeight}px`,
                                border: '1px solid #ccc',
                                overflow: 'hidden'
                            }}>
                            <iframe
                                ref={iframeRef}
                                srcDoc={auditLogs}
                                onLoad={handleIframeLoad}
                                style={{ width: '100%', height: '100%', border: 'none' }}
                                title="Audit Log Report"
                                sandbox="allow-scripts allow-same-origin"
                            />
                        </div>
                    </>
                )}
            </div>
        </ModalBody>
    </AccentDialog>
);
};